<template>
  <div :class="fullView ? 'full-view' : 'contracts-signed-adjusted-echarts'">
    <div class="chart-wrapper">
      <div class="chart-container">
        <v-chart
          class="chart pa-0"
          :option="chartOption"
          ref="chartRef"
          :init-options="{ renderer: 'svg' }"
        />
      </div>
    </div>
    <div
      class="select-container mx-auto pt-3 pb-1"
      v-if="filteredCurrencies.length > 1"
    >
      <v-select
        v-model="currency"
        :items="filteredCurrencies"
        label="Select currency"
        hide-details
        outlined
        dense
        class="currency-select mx-auto"
      />
    </div>
  </div>
</template>

<script>
import { use } from 'echarts/core'
import { BarChart } from 'echarts/charts'
import { TooltipComponent, LegendComponent } from 'echarts/components'
import { SVGRenderer } from 'echarts/renderers'
import VChart from 'vue-echarts'
import { COLOR_PALETTE, getDefaultChartTextStyle, getBarNoDataOptions } from '@/helpers/insightsCharts'
import { useChartResize } from '@/composables/useChartResize'
import { emitChartFinished } from '@/utils/insightsPdfUtils'

use([TooltipComponent, LegendComponent, BarChart, SVGRenderer])

export default {
  name: 'ContractsSignedAdjustedPricesChart',
  components: {
    VChart
  },
  props: {
    resultSet: {
      type: Object,
      required: true,
      default: () => ({})
    },
    fullView: {
      type: Boolean
    },
    selectedCurrency: {
      type: String,
      default: null
    }
  },
  setup () {
    const { chartRef, handleResize } = useChartResize()
    return {
      chartRef,
      handleResize
    }
  },
  data () {
    return {
      currencyData: {}
    }
  },
  computed: {
    currency: {
      get () {
        return this.selectedCurrency
      },
      set (value) {
        this.$emit('update:selectedCurrency', value)
      }
    },
    filteredCurrencies () {
      return Object.keys(this.currencyData).filter(currency => {
        const data = this.currencyData[currency]
        return data.signedPrice > 0 || data.adjustedPrice > 0
      })
    },
    chartOption () {
      const option = {
        animation: !this.fullView,
        title: {
          text: this.$t('insights.charts.contractsSignedAndAdjustedPrices.title', {
            currency: this.selectedCurrency ? ` (${this.selectedCurrency})` : '',
            isBreakLine: this.fullView ? ' ' : '\n'
          }),
          top: 0,
          left: 'center',
          padding: [0, 0, 10, 0],
          textStyle: getDefaultChartTextStyle(this.fullView)
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' }
        },
        legend: {
          show: false
        },
        grid: this.fullView ? { show: false } : { bottom: 40 },
        xAxis: {
          type: 'category',
          data: [
            this.$t('insights.charts.contractsSignedAndAdjustedPrices.signed', {
              isBreakLine: this.fullView ? ' ' : '\n'
            }),
            this.$t('insights.charts.contractsSignedAndAdjustedPrices.adjustments', {
              isBreakLine: this.fullView ? ' ' : '\n'
            }),
            this.$t('insights.charts.contractsSignedAndAdjustedPrices.adjusted', {
              isBreakLine: this.fullView ? ' ' : '\n'
            })
          ],
          axisLabel: {
            fontSize: this.fullView ? 13 : 10,
            fontWeight: 'bold',
            overflow: 'break'
          },
          splitLine: { show: false },
          axisLine: { show: false },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          splitLine: { show: false },
          axisLabel: { show: false },
          axisLine: { show: false }
        },
        series: []
      }

      if (this.selectedCurrency) {
        const data = this.currencyData[this.selectedCurrency]
        if (data) {
          const signedPrice = data.signedPrice / 1000000
          const adjustedPrice = data.adjustedPrice / 1000000
          const changedPrice = adjustedPrice + signedPrice

          option.tooltip = {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: (params) => {
              if (params[2]?.dataIndex === 2) return `${params[2].name}: ${changedPrice.toFixed(1)}k`
              const tar = params.find(p => p.seriesName !== 'Placeholder') || {}
              const tar2 = params.find(p => p.seriesName === 'Adjusted Bar') || { value: 0 }
              return `${tar.marker}${tar.name}: ${(tar.value + tar2.value).toFixed(1)}k`
            }
          }

          option.series = [
            {
              name: 'Placeholder',
              type: 'bar',
              stack: 'Total',
              barWidth: '35%',
              itemStyle: { borderColor: 'transparent', color: 'transparent' },
              data: [0, signedPrice, 0]
            },
            {
              name: 'Single bar',
              type: 'bar',
              stack: 'Total',
              barWidth: '35%',
              label: {
                show: true,
                position: 'top',
                ...getDefaultChartTextStyle(this.fullView),
                formatter: (params) => params.dataIndex === 2 ? '' : `${params.value.toFixed(1)}k`
              },
              itemStyle: {
                color: (params) => params.dataIndex === 1
                  ? COLOR_PALETTE[0]
                  : COLOR_PALETTE[1]
              },
              data: [signedPrice, adjustedPrice, signedPrice]
            },
            {
              name: 'Adjusted Bar',
              type: 'bar',
              stack: 'Total',
              barWidth: '35%',
              itemStyle: { color: COLOR_PALETTE[0] },
              label: {
                show: true,
                position: 'top',
                ...getDefaultChartTextStyle(this.fullView),
                formatter: (params) => params.dataIndex === 2 ? `${changedPrice.toFixed(1)}k` : ''
              },
              data: [0, 0, adjustedPrice]
            }
          ]
        }
      }
      const noDataOptions = getBarNoDataOptions(this.resultSet, this.fullView, option)
      return noDataOptions
        ? {
            ...noDataOptions,
            title: option.title
          }
        : option
    }
  },
  methods: {
    updateData () {
      const pivot = this.resultSet.chartPivot()

      this.currencyData = pivot.reduce((acc, row) => {
        const currency = row.x
        if (!currency) return acc

        if (!acc[currency]) {
          acc[currency] = {
            signedPrice: 0,
            adjustedPrice: 0
          }
        }

        acc[currency].signedPrice += row['parts.signedprice'] || 0
        acc[currency].adjustedPrice += row['parts.adjustedprice'] || 0

        return acc
      }, {})

      this.syncSelectedCurrency()
      emitChartFinished(this, false)
    },
    syncSelectedCurrency () {
      const nextCurrency = this.filteredCurrencies.includes(this.selectedCurrency)
        ? this.selectedCurrency
        : (this.filteredCurrencies[0] || null)

      if (nextCurrency !== this.selectedCurrency) {
        this.$emit('update:selectedCurrency', nextCurrency)
      }
    }
  },
  watch: {
    resultSet: {
      handler () {
        this.updateData()
      },
      deep: true
    }
  },
  mounted () {
    this.updateData()
    this.$nextTick(() => {
      window.dispatchEvent(new Event('resize'))
    })
  }
}
</script>

<style scoped>
.contracts-signed-adjusted-echarts {
  height: 250px;
  display: flex;
  flex-direction: column;
}

.full-view {
  height: var(--full-view-chart-height);
  width: var(--full-view-chart-width);
  display: flex;
  flex-direction: column;
}

.chart-wrapper {
  flex: 1 1 100%;
  min-height: 0;
  position: relative;
}

.chart-container {
  height: 100%;
  width: 100%;
  position: absolute;
}

.select-container {
  flex-shrink: 0;
  height: 56px;
}

.currency-select {
  width: 120px;
}
</style>
